import type { QueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { apiClient } from '@/shared/api/apiClient'
import { PRODUCTS_QUERY_KEY } from '@/shared/hooks/api/product'
import { PRODUCT_SOURCES_KEY } from '@/shared/hooks/api/productSource'
import { trackInFlightProductSourceUpload } from '@/features/product-catalog/lib/persistProductSourceUploadFailed'
import { uploadFileToExistingProductSource } from '@/features/product-catalog/lib/uploadFileToExistingProductSource'
import {
  assertUploadFileReadable,
  assertUploadFileSize,
  isPasswordProtectedFileError,
  isRightsProtectedFileError,
  isInvalidLegacyXlsFileError,
  isInvalidPdfFileError,
  isUploadTooLargeError,
} from '@/shared/lib/uploadGuardrails'
import {
  isProductSourceSpreadsheetOrCsvFile,
  resolveProductSourceContentType,
} from '@/features/product-catalog/utils/productSourceFileTypes'

export type PdfUploadTracking = {
  onSourceAdded?: (sourceId: string) => void
  onSourceFinished?: (sourceId: string) => void
}

async function invalidateProductSourceQueries(
  queryClient: QueryClient,
  productId: string
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [PRODUCT_SOURCES_KEY, productId] })
  await queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
}

/**
 * Creates PDF product sources then starts GCS uploads in the background (no awaiting PUT).
 * Mirrors {@link useProductFileSourceUpload} semantics and cache invalidation.
 */
export async function kickoffProductPdfSourceUploads(args: {
  productId: string
  files: File[]
  queryClient: QueryClient
  translate: TFunction<'productCatalog'>
  tracking?: PdfUploadTracking
}): Promise<void> {
  const { productId, files, queryClient, translate, tracking } = args
  if (files.length === 0) {
    return
  }

  const batchId = files.length > 1 ? crypto.randomUUID() : undefined

  const runUploadInBackground = async (file: File, sourceId: string) => {
    await uploadFileToExistingProductSource({
      productId,
      sourceId,
      file,
      queryClient,
      translate,
      onFinished: () => {
        tracking?.onSourceFinished?.(sourceId)
      },
    })
  }

  for (const file of files) {
    try {
      assertUploadFileSize(file)
      await assertUploadFileReadable(file)
    } catch (error) {
      if (isUploadTooLargeError(error)) {
        toast.error(translate('sources.source.fileTooLargeVerbatim'))
        continue
      }
      if (isPasswordProtectedFileError(error)) {
        if (isProductSourceSpreadsheetOrCsvFile(file)) {
          toast.error(translate('sources.source.fileSpreadsheetUnreadable'))
        } else {
          toast.error(translate('sources.source.fileEncrypted'))
        }
        continue
      }
      if (isRightsProtectedFileError(error)) {
        if (isProductSourceSpreadsheetOrCsvFile(file)) {
          toast.error(translate('sources.source.fileSpreadsheetUnreadable'))
        } else {
          toast.error(translate('sources.source.fileRightsProtected'))
        }
        continue
      }
      if (isInvalidPdfFileError(error)) {
        toast.error(translate('sources.source.fileInvalidPdf'))
        continue
      }
      if (isInvalidLegacyXlsFileError(error)) {
        toast.error(translate('sources.source.fileInvalidLegacyXls'))
        continue
      }
      toast.error(translate('sources.source.addError'))
      continue
    }

    try {
      const response = await apiClient.post<{ id: string }>(
        `/products/${productId}/sources`,
        {
          name: file.name,
          type: resolveProductSourceContentType(file),
          ...(batchId != null ? { initialBatchId: batchId } : {}),
        }
      )
      const sourceId = response.data.id
      tracking?.onSourceAdded?.(sourceId)
      trackInFlightProductSourceUpload(productId, sourceId)
      await invalidateProductSourceQueries(queryClient, productId)
      void runUploadInBackground(file, sourceId)
    } catch {
      toast.error(translate('sources.source.addError'))
    }
  }
}
